import {AbstractHomeActivity} from "../AbstractHomeActivity";
import {Container} from "../../Core/Container";
import {CheckmarkButton, HomeButton} from "../Button";
import {ShelfItemButton} from "../ShelfItemButton";
import {hud} from "../../Layout/HudLayout";
import {TransformBackup} from "../../Core/TransformBackup";
import {Package} from "../Package";
import {HomeScreenModel} from "../../Data/HomeScreenModel";
import {Application} from "../../Application";
import {Ease} from "../../Core/Animatable";
import {Callback} from "../../Core/Reflection";
import {GameManager} from "../../Managers/GameManager";
import {ItemType} from "../../Data/ItemModel";
import {VoiceOverManager} from "../../Managers/VoiceOverManager";
import {MusicType} from "./MusicModel";
import {Keyboard} from "../Keyboard";
import {Focusable} from "../Focusable";
import {nextRandomVO, playVO, ReactionVO, stopVO, voOptionsFacts, voOptionsIdle} from "../../Data/VoiceOver";
import {HomeScreen} from "../../States/HomeScreen";

export abstract class AbstractMusicActivity<T extends (Container & typeof TransformBackup)> extends AbstractHomeActivity {

    public readonly container = this.addChild(new Container());
    public readonly sprite = this.container.addChild(this.getSprite());
    public readonly buttonOk = this.addChild(new CheckmarkButton());
    public readonly buttonHome = this.addChild(new HomeButton());
    private shelfButton?: ShelfItemButton;

    private reactionVO?: string[];
    private playingReactionVO = false;
    private reactionIndex = 0;
    private isReplaying = false;
    private factsDisabled = false;

    constructor(screen: HomeScreen) {
        super(screen);
        this.buttonOk.visible = false;
        this.buttonHome.visible = false;
        hud.call(this);

        this.reactionVO = this.getReactionVO();
    }

    protected abstract getSprite(): T;

    protected abstract getMusicType(): MusicType;

    destroy() {
        VoiceOverManager.instance.stopVO();
        super.destroy();
    }

    open(box: Package | undefined, button: ShelfItemButton, onClose?: (model?: HomeScreenModel) => void): void {
        const itemType = box?.getModel()?.itemType ?? button.itemType;
        this.isReplaying = itemType && GameManager.instance.hasItem(itemType);
        const musicType = this.getMusicType();
        GameManager.instance.addItem({
            itemType: musicType as any,
            itemData: {musicType: musicType}
        });
        this.sprite.saveTransform();
        this.shelfButton = button;
        this.buttonOk.onClick.remove();
        this.buttonOk.onClick.add(() => {
            this.deactivate();
            this.buttonOk.moveYTo(Application.instance.size.y + this.buttonOk.height * 0.5, 0.2, Ease.sineIn);
            this.playCompleteVO(() => {
                this.zoomOutToShelf(button, () => {
                    button.visible = true;
                    button.alpha = 1;
                    this.destroy();
                    if (onClose) {
                        onClose({activityComplete: true});
                    }
                });
            });
        });
        if (box) {
            box.takeItem(this.sprite, () => {
                button.alphaTo(0, 0.2);
                this.sprite.changeParent(this.container);
                this.sprite.zIndex = 0;
                this.playStartVO();
            });
        } else {
            this.zoomInFromShelf(button, () => {
                this.playStartVO();
            });
        }
    }

    private playStartVO() {
        const vo = this.getStartVO();
        if (vo) {
            playVO(vo, () => {
                this.delayedCall(3, () => {
                    const vo = this.getFactVO();
                    if (vo && !this.factsDisabled) {
                        playVO(vo, voOptionsFacts);
                    }
                });
                if (!this.isReplaying) {
                    this.activate();
                }
            });
            if (this.isReplaying) {
                this.activate();
            }
        } else {
            this.activate();
        }
    }

    private playCompleteVO(cb?: Callback) {
        if (this.reactionVO) {
            this.factsDisabled = true;
            stopVO();
            playVO([...ReactionVO(), nextRandomVO(this.reactionVO!)], cb);
        } else if (cb) {
            cb();
        }
    }

    protected playReactionVO() {
        // Reaction VO is played on complete
        this.showOkButton();
        return;
        if (!this.reactionVO) {
            this.showOkButton();
        } else if (!this.playingReactionVO) {
            this.playingReactionVO = true;
            if (this.reactionIndex % 2 === 0) {
                playVO([...ReactionVO(), nextRandomVO(this.reactionVO!)], {
                    ...voOptionsIdle,
                    callback: () => {
                        this.playingReactionVO = false;
                        this.showOkButton();
                    }
                });
            } else {
                this.delayedCall(10, () => this.playingReactionVO = false);
                this.showOkButton();
            }
            this.reactionIndex++;
        }
    }

    protected activate() {
        this.sprite.saveTransform();
        this.initKeyboard();
        if (this.isReplaying) {
            this.showOkButton();
        }
    }

    protected deactivate() {
        this.buttonOk.enabled = false;
        Keyboard.clear();
    }

    protected showOkButton(delay = 0) {
        if (!this.buttonOk.visible) {
            this.buttonOk.visible = true;
            this.buttonOk.moveYFrom(Application.instance.size.y + this.buttonOk.height * 0.5, 0.4, Ease.backOut, {
                delay: delay,
            });
        }
    }

    protected zoomInFromShelf(button: ShelfItemButton, cb: Callback) {
        this.overlay.show(0.6);
        button.visible = false;
        const sprite = button.getItemVisual()!;
        sprite.parent.addChild(this.sprite);
        this.sprite.copyFrom(sprite, "x", "y", "width", "height", "rotation");
        this.sprite.changeParent(this.container);
        this.sprite.zIndex = 0;
        this.sprite.tweenTo<Container>(0.6, Ease.sineInOut, {
            ...this.sprite.getTransformBackup(),
            onComplete: cb
        });
    }

    protected zoomOutToShelf(button: ShelfItemButton, cb: Callback) {
        this.overlay.hide(0.6);
        button.visuals.scale.set(1);
        button.visuals.position.set(0);
        let sprite = button.getItemVisual();
        if (!sprite) {
            button.setItem(GameManager.instance.getItem(ItemType.Glockenspiel)!);
            sprite = button.getItemVisual()!;
        }
        sprite.parent.addChild(this.sprite);
        this.sprite.copyFrom(sprite, "x", "y", "width", "height", "rotation");
        this.sprite.changeParent(this.container);
        this.sprite.zIndex = 0;
        this.sprite.tweenFrom<Container>(0.6, Ease.sineInOut, {
            ...this.sprite.getTransformBackup(),
            onComplete: cb
        });
    }

    protected initKeyboard() {
        if ((this.sprite as any as typeof Focusable).getFocusPosition) {
            Keyboard.add(this.sprite as any);
        }
        Keyboard.add(this.buttonOk);
    }

    protected getStartVO(): string | undefined {
        return "LetsGoLuna_196";
    }

    protected getFactVO(): string | undefined {
        return undefined;
    }

    protected getReactionVO(): string[] | undefined {
        return undefined;
    }
}